import React from 'react'
import SpinnerIcon from '../Icons/SpinnerIcon'
import { IcaDialog } from '../IcaElements/IcaDialog'
import { IcaHeader } from '../IcaElements/IcaHeader'
import { IcaPlaceholder } from '../IcaElements/IcaPlaceholder'
import { useDeviceContext } from '../../contexts/device-context'
import StoreGroceryStock from '../../types/StoreGroceryStock'
import { IcaAlertMessage } from '../IcaElements/IcaAlertMessage'
import { IcaCardRow } from '../IcaElements/IcaCardRow'
import { IcaRadio } from '../IcaElements/IcaRadio'
import { IcaCheckbox } from '../IcaElements/IcaCheckbox'
import { IcaMeta } from '../IcaElements/IcaMeta'
import { IcaMediaObject } from '../IcaElements/IcaMediaObject'
import ItemImage from '../ItemImage/ItemImage'
import { IcaButton } from '../IcaElements/IcaButton'
import '@ica-azure/ica-elements/IcaIconPlaceholder'

export interface IGroceryFeedCleanupProps {
  feed: Array<StoreGroceryStock>
  loading?: boolean
  showCleanupDialog: boolean
  setShowCleanupDialog: (show: boolean) => void
  onDeleteMultiple: (eansToDelete: string[]) => Promise<void>
}

type MonthsFilter = 1 | 3 | 6

export default function GroceryFeedCleanup({
  feed,
  loading,
  showCleanupDialog,
  setShowCleanupDialog,
  onDeleteMultiple,
}: IGroceryFeedCleanupProps) {
  const { deviceState } = useDeviceContext()

  const [filteredArticles, setFilteredArticles] = React.useState<Array<StoreGroceryStock>>([])
  const [selectedMonthFilter, setSelectedMonthFilter] = React.useState<MonthsFilter>(1)
  const [selectedArticles, setSelectedArticles] = React.useState<Array<string>>([])

  const filterByMonths = React.useCallback((months: number) => {
    const today = new Date()
    const cutoffDate = new Date()
    cutoffDate.setMonth(today.getMonth() - months)

    const filteredFeed = feed?.filter((item: StoreGroceryStock) => {
      const itemDate = new Date(item.restockedAt)
      return itemDate < cutoffDate
    })

    setFilteredArticles(filteredFeed || [])
    setSelectedArticles(filteredFeed?.map((item: StoreGroceryStock) => item.ean) || [])
  }, [feed])

  React.useEffect(() => {
    if (feed) {
      filterByMonths(selectedMonthFilter)
    }
  }, [feed, filterByMonths, selectedMonthFilter])

  // Reset and select all articles when dialog opens
  React.useEffect(() => {
    if (showCleanupDialog && feed) {
      filterByMonths(selectedMonthFilter)
    }
  }, [showCleanupDialog, feed, filterByMonths, selectedMonthFilter])

  const handleSelectArticle = (ean: string) => {
    if (selectedArticles.includes(ean)) {
      setSelectedArticles(selectedArticles.filter(selectedEan => selectedEan !== ean))
    } else {
      setSelectedArticles([...selectedArticles, ean])
    }
  }

  const cleanupSelectedArticles = async () => {
    if (selectedArticles.length === 0) {
      return
    }

    await onDeleteMultiple(selectedArticles)
    closeDialog()
  }

  const closeDialog = () => {
    setSelectedMonthFilter(1)
    setShowCleanupDialog(false)
  }

  return (
    <IcaDialog
      open={showCleanupDialog}
      onClose={closeDialog}
      slot='main'
      snaptotop
      desktopfullheight
      disableBackdropClose={deviceState.isMobile ? false : true}
    >
      <IcaHeader
        slot='header'
        variant='dialog'
        heading='Rensa i sortimentet'
        leftaction='close'
        onLeftactionclick={closeDialog}
      />

      {loading &&
          <div className='grid place-content-center mt-[30vh]'>
            <SpinnerIcon />
          </div>
      }

      {!loading && (
        <div className='bg-[#F9F5F5]'>
          <div className='p-4 bg-white'>
            <IcaAlertMessage
              alarmtype='info'
              persistencetype='persistent'
              prominencetype="subtle"
              text='Ta bort artiklar från skåpets sortiment som inte fyllts på under en viss tid. Fyller du på en borttagen artikel i skåpet igen läggs den tillbaka i sortimentet.'
              noclose
            />
          </div>

          <p className='font-bold px-4 py-2 text-[#3A3A3A] leading-[18px]'>Ta bort artiklar som inte fyllts på...</p>
          <IcaRadio checked={selectedMonthFilter === 1} onChange={() => { setSelectedMonthFilter(1); filterByMonths(1) }} label='Senaste månaden' row />
          <IcaRadio checked={selectedMonthFilter === 3} onChange={() => { setSelectedMonthFilter(3); filterByMonths(3) }} label='Senaste 3 månaderna' row />
          <IcaRadio checked={selectedMonthFilter === 6} onChange={() => { setSelectedMonthFilter(6); filterByMonths(6) }} label='Senaste 6 månaderna' row />

          <div className='px-4 py-2'>
            <IcaCheckbox
              checked={selectedArticles.length > 0 || selectedArticles.length === filteredArticles.length && selectedArticles.length > 0}
              multiple={selectedArticles.length > 0 && selectedArticles.length < filteredArticles.length}
              onChange={() => {
                if(selectedArticles.length === filteredArticles.length) {
                  setSelectedArticles([])
                  return
                } else if(selectedArticles.length === 0) {
                  setSelectedArticles(filteredArticles.map(item => item.ean))
                } else if(selectedArticles.length > 0 && selectedArticles.length < filteredArticles.length) {
                  setSelectedArticles([])
                }
              }}
            >
              <p className='text-xs font-bold uppercase'>Artiklar</p>
            </IcaCheckbox>
          </div>
              
          {filteredArticles.length > 0 ? filteredArticles.map(item => {
            return (
              <IcaCardRow noninteractive key={item.ean}>
                <div className='flex flex-row items-center gap-2'>
                  <IcaCheckbox
                    checked={selectedArticles.includes(item.ean)}
                    onChange={() => handleSelectArticle(item.ean)}
                  />
                  <IcaMediaObject align='center'>
                    <ItemImage slot='first' ean={item.ean} />
                    <div slot='last'>
                      <IcaMeta
                        heading={item.name}
                        subheading={item.ean}
                        text={`<div>Senast påfylld: ${new Date(item.restockedAt).toLocaleDateString('sv-SE')}, <span>Antal i skåp: ${item.quantity} st</span></div>`}
                      />
                    </div>
                  </IcaMediaObject>
                </div>
              </IcaCardRow>)
          }) : (
            <IcaPlaceholder text='Inga artiklar att visa'>
              <tgdb-ica-icon-placeholder />
            </IcaPlaceholder>
          )}
              
        </div>
      )}

      {(!loading && selectedArticles && selectedArticles.length > 0) && (
        <>
          <IcaButton
            slot="buttons"
            text="Rensa valda artiklar"
            onClick={cleanupSelectedArticles}
            fullwidth
          />
          <IcaButton
            slot="buttons"
            text="Avbryt"
            variation="secondary"
            onClick={closeDialog}
            fullwidth
          />
        </>
      )}

    </IcaDialog>
  )
}
